import React, { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useEnvironment } from '../components/EnvironmentContext';
import { ENVIRONMENT_DEMO } from '../../constants';
import { useTranslation } from 'react-i18next';
import { ProductGroupDropdown } from './ProductGroupDropdown/ProductGroupDropdown';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { MATERIAL_ICON_FAVORITE } from './Button/Icon';
import { LibraryManagedProductGroupOnSiteOut } from '../../store/api-catman';

const CLASSNAME_PREFIX = `librarymanaged-pg-dropdown`;
const StBulkActionDropdown = styled(ProductGroupDropdown)<{ classNamePrefix: string; willOpenToTheLeft: boolean }>`
    button {
        padding: 0 0.6em;
    }

    .${CLASSNAME_PREFIX}__menu-list, .${CLASSNAME_PREFIX}__menu {
        width: 320px !important;
        z-index: 150;
        right: ${({ willOpenToTheLeft }) => (willOpenToTheLeft ? 0 : 'inherit')};
`;

const StLabel = styled.span`
    word-break: break-word;
`;

type Props = {
    readonly onListClick: (productGroupId: number) => void;
    readonly productGroups?: ReadonlyArray<LibraryManagedProductGroupOnSiteOut>;
    readonly className?: string;
    readonly widthInPixel?: number;
    readonly dataDisplayNameValueContainer?: string;
    readonly isNothingSelected?: boolean;
};

const LibraryManagedProductGroupDropdown: FC<Props> = ({
    onListClick,
    productGroups = [],
    className,
    widthInPixel = 320,
    dataDisplayNameValueContainer,
    isNothingSelected
}) => {
    const { t } = useTranslation();
    const [willOpenToTheLeft, setWillOpenToTheLeft] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (dropdownRef && dropdownRef.current) {
            const dropdownMenuWidth = widthInPixel;
            const viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
            const leftPositionOfDropdown = dropdownRef.current?.getBoundingClientRect().left;
            const calculatedRightPositionOfDropdown = leftPositionOfDropdown + dropdownMenuWidth;
            if (calculatedRightPositionOfDropdown >= viewportWidth) {
                setWillOpenToTheLeft(true);
            }
        }
    }, [widthInPixel]);

    const environment = useEnvironment();

    // CatMan is not available in the demo environment
    return environment !== ENVIRONMENT_DEMO && productGroups.length > 0 ? (
        <div ref={dropdownRef}>
            <StBulkActionDropdown
                willOpenToTheLeft={willOpenToTheLeft}
                key="addToGroup"
                className={className}
                actionIcon={MATERIAL_ICON_FAVORITE}
                displayValue={t('addToGroup')}
                onChange={option => {
                    onListClick((option as { label: Element; value: number }).value);
                }}
                options={productGroups.map(group => {
                    const scope = group.scope?.split('-')[0];
                    const loanFormatValue = mapLoanFormat(scope)?.toLowerCase();
                    return {
                        label: (
                            <StLabel>
                                {t(`components.productGroupScopeDropdown.allAges.${loanFormatValue}_name`, {
                                    defaultValue: t('components.productGroupScopeDropdown.discover_name'),
                                    productGroupName: group.productGroupName
                                })}
                            </StLabel>
                        ),
                        value: group.productGroupId
                    };
                })}
                isDisabled={isNothingSelected}
                classNamePrefix={CLASSNAME_PREFIX}
                dataDisplayNameValueContainer={dataDisplayNameValueContainer}
            />
        </div>
    ) : null;
};

export default LibraryManagedProductGroupDropdown;
